/**
 * Admin cookie-auth CSRF header (G23 / G7 twin of customer `customerAuthCsrf.ts`).
 * Must match backend `requireAdminAuthCsrfHeader` / `ADMIN_AUTH_CSRF_HEADER_VALUE`.
 *
 * Attach `X-Requested-With: XMLHttpRequest` on every Locked CSRF Yes mutation under
 * dual|cookie — session ops, oidc/start, account self-service, demo-tour,
 * and G9 /dev mutators (entitlement-policy, owner-assignments, …).
 */

export const ADMIN_AUTH_CSRF_HEADER = 'X-Requested-With';
export const ADMIN_AUTH_CSRF_HEADER_VALUE = 'XMLHttpRequest';

export function adminAuthCsrfHeaders(): Record<string, string> {
  return {
    [ADMIN_AUTH_CSRF_HEADER]: ADMIN_AUTH_CSRF_HEADER_VALUE,
  };
}

/**
 * Path markers for Locked CSRF Yes mutating routes (T5 / adminAuthCsrfYesRouteMatrix).
 * Matched via `path.includes(marker)` so `/api` + `/api/v1` twins are covered.
 * Safe methods (GET/HEAD/OPTIONS) never attach — see `shouldAttachAdminAuthCsrf`.
 */
export const ADMIN_AUTH_CSRF_YES_PATH_MARKERS = [
  // Session ops — /api + /api/v1 × admin|dev
  '/admin/refresh',
  '/dev/refresh',
  '/admin/logout',
  '/dev/logout',
  '/logout-all',
  // OIDC login start — four surfaces (admin|dev × /api|/api/v1); not oidc/session establish
  '/oidc/start',
  // Phase-3 SSE ticket mint — CSRF Yes; admin-only (no /dev/events/ticket twin)
  '/admin/events/ticket',
  // Account self-service (rbacRoutes dual-mount)
  '/admin/account/username',
  '/admin/account/preferred-locale', // live-base PUT preferred-locale (G1)
  '/admin/account/password',
  '/admin/account/oidc/link',
  '/admin/account/mfa/enroll',
  '/admin/account/mfa/disable',
  '/admin/account/step-up',
  '/admin/account/payment-preferences',
  '/admin/account/configuration-progress-dismiss',
  // Wave 3 — onboarding bookmark (≠ Account dismiss)
  '/admin/tenant/onboarding-bookmark',
  // Account branding — PATCH settings + PUT/DELETE logo + PUT/DELETE wordmark (G9e1)
  '/admin/account/branding/settings',
  '/admin/account/branding/logo',
  // Sales-point store photo — PUT/DELETE light + dark (parity with account branding)
  '/admin/sales-points/',
  // Demo-tour Issue + Clear — POST session + session/clear (covers both via includes)
  '/admin/demo-tour/session',
  // CT-RBAC-345 — create tenant SUPER_ADMIN
  '/admin/tenant-admins/super-admin',
  // PR-7 / FI-27 — Owner org lifecycle + platform execute/activate (BAN bare /admin/tenants/)
  '/admin/account/organization/pause',
  '/admin/account/organization/resume',
  '/admin/account/organization/close',
  '/admin/account/organization/closure-request',
  '/admin/account/leave-organization',
  '/admin/account/identity-closure-request',
  '/closure-request/execute',
  '/activate-after-restore',
  // Existing platform lifecycle mutators (requireAdminAuthCsrfHeader)
  '/terminate-contract',
  '/reopen-contract',
  '/schedule-physical-purge',
  '/cancel-physical-purge',
  // G9 addend — keep lockstep with adminAuthCsrfYesRouteMatrix ADMIN_CSRF_YES_ADDEND_V1
  '/entitlement-policy', // CT-DEV-070 PUT /dev/tenants/:id/entitlement-policy
  '/owner-assignments', // CT-DEV-111/112 + CT-RBAC-337..339 (dev + admin tenants)
  '/promote-super-dev', // CT-DEV-113
  '/demote-dev', // CT-DEV-114
  '/session/rebind-tenant', // CT-DEV-115 + CT-RBAC-347
  '/admin/tenant-access-requests', // CT-RBAC-340 / 342..344
  '/reject-cash', // CT-RBAC-346
  // Other /dev mutators that mount requireAdminAuthCsrfHeader (not in addend list but live)
  '/product-capability-readiness',
  '/commercial-requests/',
  '/price-health/',
] as const;

/** @deprecated Prefer `isAdminAuthCsrfYesPath` — kept for call-site stability. */
export function isAdminMutatingSessionPath(path: string): boolean {
  return isAdminAuthCsrfYesPath(path);
}

export function isAdminAuthCsrfYesPath(path: string): boolean {
  return ADMIN_AUTH_CSRF_YES_PATH_MARKERS.some((marker) => path.includes(marker));
}

export function shouldAttachAdminAuthCsrf(method: string, path: string): boolean {
  const upper = method.toUpperCase();
  if (upper === 'GET' || upper === 'HEAD' || upper === 'OPTIONS') {
    return false;
  }
  return isAdminAuthCsrfYesPath(path);
}
